import { useMutation } from '@apollo/client';
import EditActionButton from '@components/EditActionButton';
import { confirm } from '@components/ConfirmModal/confirm';
import { useSnackbar } from '@components/Snackbar/SnackbarProvider';
import { GET_PRO, UPDATE_GEO_LOCATION_PRO } from '@gql/pros';
import { Box, Button, Divider, IconButton, Typography, useTheme } from '@mui/material';
import { useState } from 'react';
import { SectionCard } from '../../talents/components/SectionCard';
import { SectionTitle } from '../../talents/components/SectionTitle';
import CompanyGeoLocationForm from './CompanyGeoLocationForm';
import CompanyGeoLocationMap from './CompanyGeoLocationMap';

interface GeoLocationValues {
    lat: number;
    lng: number;
    address?: string;
    city?: string;
    postal?: string;
}

interface CompanyGeoLocationCardProps {
    pro: any;
    onUpdated?: () => void;
}

const toGeoInput = ({ lat, lng, address, city, postal }: any): GeoLocationValues => ({
    lat,
    lng,
    ...(address ? { address } : {}),
    ...(city ? { city } : {}),
    ...(postal ? { postal } : {}),
});

const CompanyGeoLocationCard = ({ pro, onUpdated }: CompanyGeoLocationCardProps) => {
    const theme = useTheme();
    const { showSnackbar } = useSnackbar();

    // null = nothing open | 'new' = add form | number = editing that index
    const [activeForm, setActiveForm] = useState<null | 'new' | number>(null);
    const [removingIndex, setRemovingIndex] = useState<number | null>(null);

    const [updateGeoLocation, { loading }] = useMutation(UPDATE_GEO_LOCATION_PRO);

    const refetchOptions = {
        refetchQueries: [{ query: GET_PRO, variables: { proId: pro?._id } }],
    };

    // Non-group: update the single location
    const handleSubmit = async ({ lat, lng, address, city, postal }: GeoLocationValues) => {
        try {
            await updateGeoLocation({
                variables: { 
                    proId: pro?._id, 
                    geoLocations: [{ lat, lng, ...(address ? { address } : {}), ...(city ? { city } : {}), ...(postal ? { postal } : {}) }],
                    ...refetchOptions,
                }
            });
            showSnackbar('Géolocalisation mise à jour avec succès', 'success');
            setActiveForm(null);
            onUpdated?.();
        } catch {
            showSnackbar('Erreur lors de la mise à jour de la géolocalisation', 'error');
        }
    };

    // Non-group: delete the single location
    const handleDelete = async () => {
        if (!await confirm({ confirmation: 'Êtes-vous sûr de vouloir supprimer cette localisation ?' })) return;
        try {
            await updateGeoLocation({
                variables: { 
                    proId: pro?._id, 
                    shouldDelete: true 
                },
                ...refetchOptions,
            });
            showSnackbar('Localisation supprimée', 'success');
            setActiveForm(null);
            onUpdated?.();
        } catch {
            showSnackbar('Erreur lors de la suppression', 'error');
        }
    };

    // Group: append a new location
    const handleAddLocation = async ({ lat, lng, address, city, postal }: GeoLocationValues) => {
        try {
            const currentLocations = (pro?.geoLocations ?? []).map(toGeoInput);
            await updateGeoLocation({
                variables: {
                    proId: pro?._id,
                    geoLocations: [
                        ...currentLocations, 
                        { 
                            lat, 
                            lng, 
                            ...(address ? { address } : {}),
                            ...(city ? { city } : {}),
                            ...(postal ? { postal } : {}),
                        }
                    ],
                },
                ...refetchOptions,
            });
            showSnackbar('Localisation ajoutée avec succès', 'success');
            setActiveForm(null);
            onUpdated?.();
        } catch {
            showSnackbar("Erreur lors de l'ajout de la localisation", 'error');
        }
    };

    // Group: update a location at a given index
    const handleEditLocation = async (index: number, { lat, lng, address, city, postal }: GeoLocationValues) => {
        try {
            const currentLocations = (pro?.geoLocations ?? []).map(toGeoInput);
            const newLocations = currentLocations.map((loc: GeoLocationValues, i: number) =>
                i === index ? { 
                    lat, 
                    lng, 
                    ...(address ? { address } : {}),
                    ...(city ? { city } : {}),
                    ...(postal ? { postal } : {}),
                } : loc
            );
            await updateGeoLocation({
                variables: { proId: pro?._id, geoLocations: newLocations },
                ...refetchOptions,
            });
            showSnackbar('Localisation mise à jour avec succès', 'success');
            setActiveForm(null);
            onUpdated?.();
        } catch {
            showSnackbar('Erreur lors de la mise à jour de la localisation', 'error');
        }
    };

    // Group: remove a location by index
    const handleRemoveLocation = async (index: number) => {
        if (!await confirm({ confirmation: 'Êtes-vous sûr de vouloir supprimer cette localisation ?' })) return;
        try {
            setRemovingIndex(index);
            const currentLocations = (pro?.geoLocations ?? []).map(toGeoInput);
            const newLocations = currentLocations.filter((_: any, i: number) => i !== index);
            await updateGeoLocation({
                variables: { proId: pro?._id, geoLocations: newLocations , shouldDelete: true},
                ...refetchOptions,
            });
            showSnackbar('Localisation supprimée', 'success');
            if (activeForm === index) setActiveForm(null);
            onUpdated?.();
        } catch {
            showSnackbar('Erreur lors de la suppression', 'error');
        } finally {
            setRemovingIndex(null);
        }
    };

    const geoLocation = pro?.geoLocations[0];
    const geoLocations: { _id?: string; lat: number; lng: number; address?: string; city?: string; postal?: string }[] = pro?.geoLocations ?? [];
    const isGroup = pro?.isGroup;

    if (isGroup) {
        return (
            <SectionCard sx={{ display: 'flex', flexDirection: 'column', gap: '12px', padding: 0, overflow: 'hidden', background: theme.palette.dashboard.main }}>
                <Box sx={{ paddingY: 0, overflow: 'hidden', flex: 1 }}>
                    <Box sx={{ padding: '12px 16px 0 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <SectionTitle title="Géolocalisations" />
                    </Box>

                    <Box sx={{ padding: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {geoLocations.length === 0 && activeForm !== 'new' && (
                            <Typography variant="body2" fontSize="13px" color="text.secondary">
                                Aucune coordonnée définie
                            </Typography>
                        )}

                        {geoLocations.map((loc, index) => (
                            <Box key={index}>
                                {index > 0 && <Divider sx={{ mb: 2 }} />}

                                {activeForm === index ? (
                                    <Box>
                                        <Typography variant="body2" fontSize="13px" fontWeight={500} sx={{ mb: 1 }}>
                                            Modifier la localisation
                                        </Typography>
                                        <CompanyGeoLocationForm
                                            initialLat={loc.lat}
                                            initialLng={loc.lng}
                                            initialAddress={loc.address}
                                            initialCity={loc.city}
                                            initialPostal={loc.postal}
                                            loading={loading}
                                            onCancel={() => setActiveForm(null)}
                                            onSubmit={(values) => handleEditLocation(index, values)}
                                        />
                                    </Box>
                                ) : (
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                            <Box sx={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
                                                <i
                                                    className="fi fi-br-marker"
                                                    style={{ fontSize: '14px', color: theme.palette.neutralWhite.main, marginTop: '2px' }}
                                                />
                                                <Box>
                                                    {loc.address && (
                                                        <Typography variant="body2" fontSize="13px" fontWeight={500}>
                                                            {loc.address}
                                                        </Typography>
                                                    )}
                                                    {(loc.postal || loc.city) && (
                                                        <Typography variant="body2" fontSize="12px" color="text.secondary">
                                                            {[loc.postal, loc.city].filter(Boolean).join(' ')}
                                                        </Typography>
                                                    )}
                                                    <Typography variant="body2" fontSize="12px" color="text.secondary">
                                                        {loc.lat.toFixed(6)}, {loc.lng.toFixed(6)}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                            <Box sx={{ display: 'flex', gap: '4px' }}>
                                                <IconButton
                                                    size="small"
                                                    onClick={() => setActiveForm(index)}
                                                    disabled={loading || activeForm !== null}
                                                    sx={{ color: theme.palette.neutralWhite.main }}
                                                >
                                                    <i className="fi fi-br-pencil" style={{ fontSize: '14px' }} />
                                                </IconButton>
                                                <IconButton
                                                    size="small"
                                                    onClick={() => handleRemoveLocation(index)}
                                                    disabled={loading || activeForm !== null}
                                                    sx={{ color: theme.palette.error.main }}
                                                >
                                                    {removingIndex === index ? (
                                                        <i className="fi fi-br-spinner" style={{ fontSize: '14px' }} />
                                                    ) : (
                                                        <i className="fi fi-br-trash" style={{ fontSize: '14px' }} />
                                                    )}
                                                </IconButton>
                                            </Box>
                                        </Box>
                                        <CompanyGeoLocationMap lat={loc.lat} lng={loc.lng} />
                                    </Box>
                                )}
                            </Box>
                        ))}

                        {activeForm === 'new' ? (
                            <Box>
                                {geoLocations.length > 0 && <Divider sx={{ mb: 2 }} />}
                                <Typography variant="body2" fontSize="13px" fontWeight={500} sx={{ mb: 1 }}>
                                    Nouvelle localisation
                                </Typography>
                                <CompanyGeoLocationForm
                                    loading={loading}
                                    onCancel={() => setActiveForm(null)}
                                    onSubmit={handleAddLocation}
                                />
                            </Box>
                        ) : (
                            <Button
                                variant="outlined"
                                size="small"
                                color="secondary"
                                onClick={() => setActiveForm('new')}
                                disabled={loading || activeForm !== null}
                                startIcon={<i className="fi fi-br-plus" style={{ fontSize: '12px' }} />}
                                sx={{ alignSelf: 'flex-start' }}
                            >
                                Ajouter une localisation
                            </Button>
                        )}
                    </Box>
                </Box>
            </SectionCard>
        );
    }

    // Non-group: single geolocation
    return (
        <SectionCard sx={{ display: 'flex', flexDirection: 'column', gap: '12px', padding: 0, overflow: 'hidden', background: theme.palette.dashboard.main }}>
            <Box sx={{ paddingY: 0, overflow: 'hidden', flex: 1 }}>
                <Box sx={{ padding: '12px 16px 0 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <SectionTitle title="Géolocalisation" />
                    {activeForm === null && <EditActionButton onClick={() => setActiveForm(0)} />}
                </Box>

                {activeForm !== null ? (
                    <Box sx={{ padding: 2 }}>
                        <CompanyGeoLocationForm
                            initialLat={geoLocation?.lat}
                            initialLng={geoLocation?.lng}
                            initialAddress={geoLocation?.address}
                            initialCity={geoLocation?.city}
                            initialPostal={geoLocation?.postal}
                            loading={loading}
                            onCancel={() => setActiveForm(null)}
                            onSubmit={handleSubmit}
                        />
                    </Box>
                ) : (
                    <Box sx={{ padding: 2 }}>
                        {geoLocation?.lat != null && geoLocation?.lng != null ? (
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                <CompanyGeoLocationMap lat={geoLocation.lat} lng={geoLocation.lng} />
                                <Box sx={{ display: 'flex', gap: '8px', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                                    <Box sx={{ display: 'flex', gap: '8px', alignItems: 'flex-start', flex: 1 }}>
                                        <i
                                            className="fi fi-br-marker"
                                            style={{ fontSize: '14px', color: theme.palette.neutralWhite.main, marginTop: '2px' }}
                                        />
                                        <Box>
                                            {geoLocation.address && (
                                                <Typography variant="body2" fontSize="13px" fontWeight={500}>
                                                    {geoLocation.address}
                                                </Typography>
                                            )}
                                            {(geoLocation.postal || geoLocation.city) && (
                                                <Typography variant="body2" fontSize="12px" color="text.secondary">
                                                    {[geoLocation.postal, geoLocation.city].filter(Boolean).join(' ')}
                                                </Typography>
                                            )}
                                            <Typography variant="body2" fontSize="12px" color="text.secondary">
                                                {geoLocation.lat.toFixed(6)}, {geoLocation.lng.toFixed(6)}
                                            </Typography>
                                        </Box>
                                    </Box>
                                    <IconButton
                                        size="small"
                                        onClick={handleDelete}
                                        disabled={loading}
                                        sx={{ color: theme.palette.error.main }}
                                    >
                                        <i className="fi fi-br-trash" style={{ fontSize: '14px' }} />
                                    </IconButton>
                                </Box>
                            </Box>
                        ) : (
                            <Typography variant="body2" fontSize="13px" color="text.secondary">
                                Aucune coordonnée définie
                            </Typography>
                        )}
                    </Box>
                )}
            </Box>
        </SectionCard>
    );
};

export default CompanyGeoLocationCard;
